/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from "@fullcalendar/interaction"
import { EventClickArg, EventSourceInput } from '@fullcalendar/core';
import ModalEntity, { Actions } from "app/modules/tp/components/Modal/ModalEntity";
import { emptyMean, Mean } from 'app/modules/tp/means/_models';
import MeanFormBuilder from 'app/modules/tp/means/builders/MeanFormBuilder';
import MeanApi from 'app/modules/tp/means/_requests';
import { ToolbarWrapper } from '_metronic/layout/components/toolbar';
import { Ride } from 'app/modules/tp/rides/_models';
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'app/modules/tp/rides/_constants';
import { IErrorResponse, IValidationErrorResponse, Response, } from "app/modules/tp/_apiResponse";
import { createErrorMessage, createSuccessMessage, createWarningMessage } from "app/partials/Alert";
import { ResourceAction } from "app/infrastructure/repository";
import ValidationError from "app/partials/form/ValidationError";
import { getStatusColor } from 'app/modules/tp/rides/utility';
import Select from 'react-select';

const MeansCalendarPage: FC = () => {
    const intl = useIntl();
    const [means, setMeans] = React.useState<Mean[]>([]);

    const [filteredMeans, setFilteredMeans] = React.useState<number[]>([]);

    const [calendarKey, setCalendarKey] = React.useState<number>(0);
    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [modalEntity, setModalEntity] = React.useState<Mean>(emptyMean);
    const [modalError, setModalError] = React.useState<JSX.Element>(<></>);
    const [modalAlertMessage, setModalAlertMessage] = React.useState<JSX.Element>(<></>);
    const [alertMessage, setAlertMessage] = React.useState<JSX.Element>(<></>);
    const entityApi = MeanApi;

    const actionApi = async (action: ResourceAction, entity: Mean, callback?: ((e: Mean) => void)) => {
        let res: Response<Mean> | undefined;
        switch (action) {
            case "create":
                res = await entityApi.create(entity)
                break;
            case "update":
                res = await entityApi.update(entity.id, entity)
                break;
            case "delete":
                res = await entityApi.delete(entity.id)
                break;
            default:
                break;
        }
        if (!(res?.success)) {
            return res;
        }
        callback && res && callback(res?.data as Mean);
        return res;
    }

    const actionAndUpdateView = async (action: ResourceAction | "save", entity: Mean) => {
        if (action === "save") {
            action = (entity.id ? "update" : "create");
        }
        const res = await actionApi(action, entity)
        if (res?.success === false) {
            if ((res as IErrorResponse)?.type === "foreign_relation") {
                return setModalError(createWarningMessage("Oops!", "Non è possibile eliminare la risorsa perché è collegata ad altre entità"));
            }
            if ((res as IErrorResponse)?.type === "validation") {
                return setModalError(ValidationError({ error: (res as IValidationErrorResponse) }));
            }
        }
        if (res?.success) {
            setModalAlertMessage(createSuccessMessage("Operazione completata!", "L'operazione è stata completata con successo"));
            await fetchMeans();
        } else {
            setModalError(createErrorMessage("Oops!", "Si è verificato un errore durante l'operazione"));
            //setAlertMessage(createErrorMessage("Oops!", "Si è verificato un errore durante l'operazione"));
        }
        return res;
    }

    const modalActions: Actions<Mean> = {
        save: {
            label: "Salva",
            onClick: async (entity: Mean) => {
                return (!!((await actionAndUpdateView("save", entity))?.success));
            }
        },
        destroy: {
            label: "Elimina",
            onClick: async (entity: Mean) => {
                if (!!((await actionAndUpdateView("delete", entity))?.success)) {
                    setAlertMessage(createSuccessMessage("Operazione completata!", "L'operazione è stata completata con successo"));
                    setShowModal(false);
                    setModalEntity(emptyMean);
                    return true;
                }
                return false;
            }
        },
        cancel: {
            label: "Chiudi",
            onClick: async (entity: Mean) => {
                setShowModal(false);
                setModalEntity(emptyMean);
                return true;
            }
        },
        duplicate: {
            label: "Duplica",
            onClick: async (entity: Mean) => {
                //TODO: implement
                return true;
            }
        },
        reload: {
            label: "Ricarica",
            onClick: async (entity: Mean) => {
                //TODO: implement
                return true;
            }
        }
    }

    const events: EventSourceInput = React.useMemo(() => {
        setCalendarKey(calendarKey + 1);
        const meanWithRides = means
            .filter((mean: Mean) => mean.rides?.length > 0)
            .flatMap((mean: Mean) => mean.rides
                ?.filter((ride: Ride) => ride.start_departure_date && ride.end_departure_date)
                .map((ride: Ride) => {
                    return (
                        {
                            title: `${mean.code ? `${mean.code} - ` : ""}${mean.license_plate}`,
                            start: moment(ride.start_departure_date, DATE_TIME_FORMAT).toDate(),
                            end: moment(ride.end_departure_date, DATE_TIME_FORMAT).toDate(),
                            id: `mr_${(mean.id).toString()}`,
                            meanId: mean.id,
                            color: getStatusColor(ride)
                        })
                })
            );

        const meanAbsent = means
            .filter((mean: Mean) => mean.meanDaysAbsences?.length > 0 || mean.meanHoursAbsences?.length > 0)
            .flatMap((mean: Mean) => [
                ...mean.meanDaysAbsences.map((absence) => ({
                    title: `${mean.code ? `${mean.code} - ` : ""}${mean.license_plate}`,
                    start: moment(absence.from, DATE_FORMAT).startOf('day').toDate(),
                    end: moment(absence.to, DATE_FORMAT).endOf('day').toDate(),
                    id: `md_${(mean.id).toString()}`,
                    meanId: mean.id,
                    color: "gray"

                })),
                ...mean.meanHoursAbsences.map((absence) => ({
                    title: `${mean.code ? `${mean.code} - ` : ""}${mean.license_plate}`,
                    start: moment(`${absence.date} ${absence.from}`, DATE_TIME_FORMAT).toDate(),
                    end: moment(`${absence.date} ${absence.to}`, DATE_TIME_FORMAT).toDate(),
                    id: `mh_${(mean.id).toString()}`,
                    color: "gray",
                    meanId: mean.id,
                }))
            ]);
        const totalMeans = [...meanWithRides, ...meanAbsent];
        return totalMeans.filter(meanEvent => {
            if (filteredMeans.length > 0 && !filteredMeans.find((meanID: number) => meanEvent?.meanId === meanID)) {
                return false;
            }
            return true;
        });
    }, [means, filteredMeans]);


    const fetchMeans = async () => setMeans((await MeanApi.getAllNoPagination())?.data ?? []);

    React.useEffect(() => {
        fetchMeans();
    }, []);

    const entityIcon = "fa-solid fa-clipboard-list";

    return (
        <>
            {
                showModal &&
                <ModalEntity<Mean>
                    key={modalEntity?.id}
                    title={{
                        icon: entityIcon,
                        label: `Scheda ${modalEntity?.email ?? "Mean"}`
                    }}
                    actions={modalActions}
                    eform={MeanFormBuilder()}
                    show={showModal}
                    initialEntity={modalEntity}
                    error={modalError}
                    message={modalAlertMessage}
                />
            }
            <ToolbarWrapper
                items={[
                    {
                        display: false,
                        component: {
                            type: 'button',
                            label: `Filtra`,
                            icon: 'fa-solid fa-plus',
                            //onClick: () => setShowCreateModal(true)
                        }
                    },
                    {
                        display: true,
                        component: {
                            type: 'custom',
                            key: "meansFilter",
                            render: () =>
                                <>
                                    <label className="form-label">Filtra per mezzo</label>
                                    <Select
                                        key="meansFilter"
                                        closeMenuOnSelect={false}
                                        isMulti
                                        options={means?.map((mean: Mean) => ({
                                            key: mean?.id,
                                            value: mean?.id,
                                            label: `${mean?.code ?? ""} ${mean?.license_plate ?? ""}`
                                        }))}
                                        onChange={(e) => {
                                            setFilteredMeans(e?.map((e: { key: number }) => e?.key) ?? []);
                                        }}
                                    />
                                </>
                        },
                    },
                ]}
            />
            {
                alertMessage
            }
            <div className={'app-container'}>
                {/* begin::Row */}
                <div className='row g-5 g-xl-10 mb-5 mb-xl-10'>
                    {/* begin::Col */}
                    <div className='col-xxl-12'>
                        <div className="card card-custom card-flush">
                            <div className="card-body">
                                <FullCalendar
                                    key={calendarKey}
                                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                    locale={intl.locale}
                                    headerToolbar={{
                                        left: "prev,next,today",
                                        center: "title",
                                        right: ""
                                    }}
                                    firstDay={1}
                                    initialView="dayGridMonth"
                                    events={events}
                                    eventClick={async (info: EventClickArg) => {
                                        const entityID = parseInt(info.event.id.split("_")[1]);
                                        const actionEntity = (await MeanApi.getFull(entityID, {}))?.data ?? emptyMean;
                                        setModalEntity(actionEntity);
                                        setShowModal(true);
                                    }}

                                />
                            </div>
                        </div>

                    </div>
                    {/* end::Col */}
                </div>
                {/* end::Row */}
            </div>
        </>
    );
};

const MeansCalendarWrapper: FC = () => {
    return (
        <>
            <MeansCalendarPage />
        </>
    )
};

export { MeansCalendarWrapper }
